import { minPeakTiles, tileGoldLine, TileModel, type TileRules } from "@nano/puzzle-core";
import { z } from "zod";
import { JADE_SYMBOL_BY_ID } from "./symbols.ts";

export const TILE_HOUSE_ID = "tiles" as const;

/** Phones keep cells at 44 px or more (360 x 640 with the companion strip) up to this many columns and rows. */
export const TILE_FLOOR_LIMITS = { columns: 6, rows: 7 } as const;

const TOKEN = String.raw`(?:\.|#|@|T|[A-H]|[1-3]|\??[a-h]{1,2})`;

export const tileRulesSchema = z
  .object({
    floor: z
      .array(z.string().regex(new RegExp(`^${TOKEN}( ${TOKEN})*$`)))
      .min(1)
      .max(TILE_FLOOR_LIMITS.rows),
    symbols: z
      .array(z.string().refine((id) => JADE_SYMBOL_BY_ID.has(id), "unknown jade symbol"))
      .min(1)
      .max(8),
    bag: z.number().int().min(3).max(7),
  })
  .strict();

export const tileLevelSchema = z
  .object({
    id: z.number().int().min(1).max(100),
    houseId: z.literal(TILE_HOUSE_ID),
    contentVersion: z.string().regex(/^tiles-\d+\.\d+\.\d+$/),
    chapter: z.number().int().min(1).max(10),
    difficulty: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]),
    themeId: z.string().min(1),
    tutorialId: z.string().min(1).optional(),
    /** Tidiest satchel any route can keep, proven by the solver when the level was frozen. */
    minPeak: z.number().int().min(1).max(7),
    /** The 3-star line on the satchel. */
    goldLine: z.number().int().min(1).max(7),
    seed: z.number().int().nonnegative().optional(),
    rules: tileRulesSchema,
  })
  .strict();

export type TileLevel = z.infer<typeof tileLevelSchema>;

export const tilePackSchema = z
  .object({
    packVersion: z.literal(1),
    houseId: z.literal(TILE_HOUSE_ID),
    contentVersion: z.string().regex(/^tiles-\d+\.\d+\.\d+$/),
    chapter: z.number().int().min(1).max(10),
    levels: z.array(tileLevelSchema).length(10),
  })
  .strict();

export type TilePack = z.infer<typeof tilePackSchema>;

export function toTileRules(level: TileLevel): TileRules {
  return level.rules as TileRules;
}

/** Checks a level against the rules and the solver: problems, or an empty list. */
export function validateTileLevel(level: TileLevel, options: { solve?: boolean } = {}): string[] {
  const problems: string[] = [];
  let model: TileModel;
  try {
    model = new TileModel(toTileRules(level));
  } catch (error) {
    return [`level ${level.id}: ${(error as Error).message}`];
  }
  if (model.width > TILE_FLOOR_LIMITS.columns) problems.push(`level ${level.id}: wider than ${TILE_FLOOR_LIMITS.columns} cells`);
  if (model.cells[model.start]?.y !== model.height - 1) problems.push(`level ${level.id}: the entrance belongs on the bottom row`);
  const used = new Set(model.totals.keys());
  if (level.rules.symbols.some((symbol) => !used.has(symbol))) problems.push(`level ${level.id}: lists a symbol it never uses`);
  if (level.goldLine !== tileGoldLine(level.minPeak, level.rules.bag)) {
    problems.push(`level ${level.id}: gold line should be ${tileGoldLine(level.minPeak, level.rules.bag)}`);
  }
  if (options.solve !== false) {
    const solved = minPeakTiles(model, { maxNodes: 2_000_000 });
    if (solved.status !== "solved") problems.push(`level ${level.id}: solver says ${solved.status}`);
    else if (solved.peak !== level.minPeak) problems.push(`level ${level.id}: tidiest satchel is ${solved.peak}, data says ${level.minPeak}`);
  }
  return problems;
}
